export const getFileNameFromContentDisposition = (
  header: string | undefined,
  fallback: string
) => {
  if (!header) return fallback;

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/['"]/g, '').trim());
  }

  const basicMatch = /filename="?([^";]+)"?/i.exec(header);
  if (basicMatch?.[1]) {
    return basicMatch[1].trim();
  }

  return fallback;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const toPrintDocument = (html: string, title: string) => {
  const safeTitle = escapeHtml(title.replace(/\.(pdf|html)$/i, ''));
  if (/<html[\s>]/i.test(html)) {
    if (/<title>[\s\S]*?<\/title>/i.test(html)) {
      return html.replace(
        /<title>[\s\S]*?<\/title>/i,
        `<title>${safeTitle}</title>`
      );
    }
    return html.replace(
      /<head([^>]*)>/i,
      `<head$1><title>${safeTitle}</title>`
    );
  }

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
  </head>
  <body>
    ${html}
  </body>
</html>`;
};

export const printHtmlAsPdf = (html: string, fileName: string) => {
  // Do not pass noopener here — browsers return null from window.open when
  // noopener is set, which incorrectly looks like a blocked pop-up.
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Pop-up blocked. Allow pop-ups to save the PDF.');
  }

  const documentHtml = toPrintDocument(html, fileName);
  printWindow.document.open();
  printWindow.document.write(documentHtml);
  printWindow.document.close();

  const triggerPrint = () => {
    printWindow.focus();
    printWindow.print();
  };

  if (printWindow.document.readyState === 'complete') {
    window.setTimeout(triggerPrint, 300);
  } else {
    printWindow.onload = () => window.setTimeout(triggerPrint, 300);
  }
};

export type DocumentPreview =
  | {
      kind: 'html';
      html: string;
      fileName: string;
    }
  | {
      kind: 'pdf';
      pdfUrl: string;
      fileName: string;
      blob: Blob;
    };

const isPdfBlob = async (blob: Blob, contentType: string) => {
  if (contentType.includes('application/pdf')) return true;
  if (contentType.includes('text/html')) return false;

  const header = await blob.slice(0, 5).text();
  return header.startsWith('%PDF-');
};

export const parseDocumentPreviewResponse = async (
  response: {
    data: Blob;
    headers: Record<string, unknown>;
  },
  fallbackFileName: string
): Promise<DocumentPreview> => {
  const contentDisposition = response.headers['content-disposition'] as
    | string
    | undefined;
  const contentType = String(response.headers['content-type'] ?? '');
  const fileName = getFileNameFromContentDisposition(
    contentDisposition,
    fallbackFileName
  );
  const blob = response.data;

  if (await isPdfBlob(blob, contentType)) {
    return {
      kind: 'pdf',
      pdfUrl: URL.createObjectURL(blob),
      fileName,
      blob
    };
  }

  return {
    kind: 'html',
    html: await blob.text(),
    fileName
  };
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const revokeDocumentPreview = (preview: DocumentPreview | null) => {
  if (preview?.kind === 'pdf') {
    URL.revokeObjectURL(preview.pdfUrl);
  }
};
